import React, { useState, useEffect } from "react";
import styled from "styled-components";
import useUserStore from "../stores/userStore";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  ActionIcon,
  CopyButton,
  Tooltip,
  Button,
  Modal,
  Stack,
  TextInput,
  Checkbox,
  Title,
  Group,
  Text,
  Alert,
  Divider,
  Paper,
} from "@mantine/core";
import { IconInfoCircle, IconUsersPlus, IconCopy, IconCheck, IconLink } from "@tabler/icons-react";
import fetchApi from "../components/Lib/api";
import Notification from "../components/Lib/Notification";
import useLayoutStore from "../stores/useLayoutStore";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import Mixpanel from "../components/Lib/mixpanel";

// Styled Components
const StartupButtonContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const DropdownIcon = styled.span`
  margin-left: 10px;
  display: flex;
  align-items: center;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  margin-top: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

const DropdownLink = styled.a`
  display: flex;
  align-items: center;
  padding: 10px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  width: 100%;

  &:hover {
    background-color: #f2f2f2;
  }
`;

function InviteUser({ activeDomain }) {
  const [notification, setNotification] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const form = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      addToSubdomain: true,
    },
    // Optionally, you can add validation rules here
    validate: {
      firstName: (value) => (value ? null : "First name is required"),
      lastName: (value) => (value ? null : "Last name is required"),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
    },
  });

  const handleSubmit = async (values) => {
    setIsLoading(true);
    setNotification(null);
    Mixpanel.form("Invite User Form", "Attempted", {
      form_data: {
        firstName_present: !!values.firstName,
        lastName_present: !!values.lastName,
        email_valid: /^\S+@\S+$/.test(values.email),
        add_to_subdomain_checked: values.addToSubdomain,
        active_domain_name: activeDomain ? activeDomain.name : "none",
      },
      submission_type: "direct_invite_form",
    });

    const startTime = performance.now();

    try {
      const response = await fetchApi(
        "/users/sessions/invitations/create_and_invite",
        "POST",
        {
          user: {
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            add_to_subdomain: values.addToSubdomain,
          },
          active_domain_name: activeDomain ? activeDomain.name : "",
        }
      );

      const endTime = performance.now(); // End timer for API call
      const responseTime = Math.round(endTime - startTime);

      if (response.ok) {
        const msg = (await response.json()).message;
        setNotification({ type: "success", content: msg });
        Mixpanel.form("Invite User Form", "Submitted", {
          form_data: {
            add_to_subdomain: values.addToSubdomain,
            active_domain_name: activeDomain ? activeDomain.name : "none",
          },
          response_time_ms: responseTime,
          status: "success",
          submission_type: "direct_invite_form",
        });
        form.reset();
      } else {
        const msg = (await response.json()).error;
        setNotification({ type: "failure", content: msg });

        Mixpanel.error("User Invitation Error", {
          error_message: msg,
          error_type: "API Error",
          http_status: response.status,
          component: "InviteUser",
          user_action: "Submit Invite Form",
          target_domain: activeDomain ? activeDomain.name : "general_site",
          response_time_ms: responseTime,
        });
      }
    } catch (error) {
      const endTime = performance.now(); // End timer even on network error
      const responseTime = Math.round(endTime - startTime);
      console.error("Error during user invitation:", error);

      Mixpanel.error("User Invitation Error", {
        error_message: error.message,
        error_type: "Network/Client Error",
        component: "InviteUser",
        user_action: "Submit Invite Form",
        target_domain: activeDomain ? activeDomain.name : "general_site",
        response_time_ms: responseTime,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {opened && (
        <Modal
          opened={opened}
          onClose={close}
          size="lg"
          radius="lg"
          transitionProps={{ transition: "fade", duration: 200 }}
          withCloseButton={true}
          yOffset={"1vh"}
          centered
        >
          <Stack spacing={20} className="p-6">
            {/* Header Section */}
            <div className="text-center">
              <Title order={3} className="text-gray-900 font-semibold mb-3">
                {t("invite.header")}
              </Title>
              <Text size="md" color="dimmed" className="text-gray-600">
                {t("invite.details")}
              </Text>
            </div>

            {/* Notification */}
            {notification && <Notification {...notification} />}

            {/* Copy Link Section */}
            {activeDomain && activeDomain.signup_url ? (
              <Paper className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <IconLink size={18} className="text-blue-600" />
                  <Title order={5} className="text-blue-900 font-medium">
                    Quick Invite Link
                  </Title>
                </div>
                
                <Text size="sm" color="dimmed" className="text-blue-700 mb-3">
                  {t("invite.alternate")}
                </Text>
                
                <div className="bg-white rounded-md p-2 border border-blue-200 mb-3">
                  <Text size="xs" className="text-gray-600 font-mono break-all">
                    {activeDomain.signup_url}
                  </Text>
                </div>
                
                <CopyButton value={activeDomain.signup_url} timeout={2000}>
                  {({ copied, copy }) => (
                    <Button
                      variant="filled"
                      color="blue"
                      size="sm"
                      fullWidth
                      onClick={() => {
                        copy();
                        Mixpanel.share("Domain Invite Link", "Clipboard", {
                          domain_name: activeDomain.name,
                          signup_url: activeDomain.signup_url,
                          action_status: copied ? "already_copied" : "copied_now",
                        });
                      }}
                      leftIcon={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      className="font-medium"
                    >
                      {copied ? "Link Copied!" : "Copy Invite Link"}
                    </Button>
                  )}
                </CopyButton>
              </Paper>
            ) : (
              <Alert
                icon={<IconInfoCircle size={16} />}
                title="General Site Invitation"
                color="blue"
                variant="light"
                size="sm"
                className="border-l-4 border-blue-500"
              >
                <Text size="sm" className="text-blue-800">
                  You are inviting this user to the general site, not a specific domain.
                </Text>
              </Alert>
            )}

            {/* Section Divider */}
            <Divider 
              label="OR" 
              labelPosition="center" 
              className="my-2"
              labelProps={{
                size: "xs",
                color: "dimmed",
                className: "font-medium text-gray-500"
              }}
            />

            {/* Form Section */}
            <Paper className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <Title order={5} className="text-gray-800 font-semibold mb-4 flex items-center gap-2">
                <IconUsersPlus size={18} className="text-teal-600" />
                Direct Invitation Form
              </Title>
              
              <Stack spacing={16}>
                <Group grow>
                  <TextInput
                    size="md"
                    label={t("invite.first_name")}
                    placeholder="Enter first name"
                    {...form.getInputProps("firstName")}
                    disabled={isLoading}
                    classNames={{
                      label: "text-gray-700 font-medium mb-2 text-sm",
                      input: "border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200",
                    }}
                  />
                  <TextInput
                    size="md"
                    label={t("invite.last_name")}
                    placeholder="Enter last name"
                    {...form.getInputProps("lastName")}
                    disabled={isLoading}
                    classNames={{
                      label: "text-gray-700 font-medium mb-2 text-sm",
                      input: "border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200",
                    }}
                  />
                </Group>
                
                <TextInput
                  size="md"
                  label={t("invite.email")}
                  type="email"
                  placeholder="Enter email address"
                  {...form.getInputProps("email")}
                  disabled={isLoading}
                  classNames={{
                    label: "text-gray-700 font-medium mb-2 text-sm",
                      input: "border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200",
                  }}
                />
                
                {/* Subdomain Checkbox */}
                {activeDomain && activeDomain.signup_url && (
                  <Checkbox
                    label={`Also add user to ${activeDomain.name}`}
                    {...form.getInputProps("addToSubdomain", { type: "checkbox" })}
                    disabled={isLoading}
                    classNames={{
                      label: "text-gray-700 text-sm font-medium",
                    }}
                  />
                )}
              </Stack>
            </Paper>

            {/* Submit Button */}
            <Button
              onClick={() => handleSubmit(form.values)}
              variant="filled"
              color="teal"
              size="md"
              fullWidth
              disabled={
                isLoading ||
                !form.values.firstName ||
                !form.values.lastName ||
                !form.values.email
              }
              className="font-semibold text-white shadow-lg hover:shadow-xl transition-shadow"
              leftIcon={isLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <IconUsersPlus size={16} />}
            >
              {isLoading ? "Sending Invitation..." : t("invite.submit_button")}
            </Button>
          </Stack>
        </Modal>
      )}

      <Tooltip
        label={t("invite.header")}
        withArrow
        position="right"
        color="teal"
        p={12}
      >
        <ActionIcon color="teal" variant="subtle" onClick={open}>
          <IconUsersPlus size={18} />
        </ActionIcon>
      </Tooltip>
    </>
  );
}

const StartupButton = () => {
  const {
    user: {
      startups,
      domains,
      user: { full_name, avatar },
    },
  } = useUserStore();

  const [startupDropdownOpen, setStartupDropdownOpen] = useState(false);
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [activeStartup, setActiveStartup] = useState(null);
  const [activeDomain, setActiveDomain] = useState(null);
  const { isToggle } = useLayoutStore();

  // Insert a main site domain entry
  const domainsWithMain = [{ name: "Main Site", subdomain: "" }, ...domains];

  useEffect(() => {
    const currentHostname = window.location.hostname;
    const currentSubdomain =
      currentHostname.split(".")[0] === "example"
        ? ""
        : currentHostname.split(".")[0];
    const matchedDomain = domainsWithMain.find(
      (domain) => domain.domain === currentSubdomain
    );

    if (matchedDomain) setActiveDomain(matchedDomain);
    else setActiveDomain(domainsWithMain[0]);

    const currentSlug = window.location.pathname.split("/").pop();
    const matchedStartup = startups.find(
      (startup) => startup.slug === currentSlug
    );

    if (matchedStartup) setActiveStartup(matchedStartup);
  }, []);


  const toggleDomainDropdown = () => {
    setDomainDropdownOpen(!domainDropdownOpen);
    setStartupDropdownOpen(false);
  };


  const handleDomainSwitch = (domain) => {
    if (domain === activeDomain) {
      setDomainDropdownOpen(false);
      return;
    }

    Mixpanel.navigation('Domain Switch Attempted', {
      old_domain_name: activeDomain?.name || 'Main Site',
      old_domain_subdomain: activeDomain?.domain || 'main_site',
      new_domain_name: domain.name,
      new_domain_subdomain: domain.domain || 'main_site',
      navigation_type: 'dropdown_selection',
      source_element: 'Domain Dropdown'
    });

    setActiveDomain(domain);
    setDomainDropdownOpen(false);

    const currentUrl = new URL(window.location.href);
    const domainParts = currentUrl.hostname.split(".");

    // Remove port if it exists
    const port = currentUrl.port ? `:${currentUrl.port}` : "";

    // Extract the base domain (last two parts of the hostname)
    // For example, from "sub.example.com", get "example.com"
    const baseDomain =
      domainParts.length > 2
        ? domainParts.slice(-2).join(".")
        : currentUrl.hostname;

    // Construct the new URL
    let newHostname;
    if (domain.domain) {
      newHostname = `${domain.domain}.${baseDomain}`;
    } else {
      newHostname = baseDomain;
    }

    // Reconstruct the full URL with the new hostname and port
    const newUrl = `${currentUrl.protocol}//${newHostname}${port}${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;

    window.location.href = newUrl;
  };

  if (isToggle) return <></>;

  return (
    <StartupButtonContainer>
      {/* Domain Selector */}
      <div className="relative inline-block w-64 w-full">
        <div className="flex items-center gap-2">
          <button
            onClick={toggleDomainDropdown}
            className="flex items-center justify-between w-full p-3 bg-white rounded-lg shadow-sm hover:bg-gray-50"
          >
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-3"></div>
              {/* Display active domain or main site */}
              <span className="text-gray-900">{activeDomain?.name}</span>
            </div>
            <DropdownIcon>
              <svg
                className={`w-2 h-2 transform ${
                  domainDropdownOpen ? "rotate-180" : ""
                }`}
                viewBox="0 0 8 5"
                fill="none"
              >
                <path d="M4 4.25L0.25 0.5H7.75L4 4.25Z" fill="#514D45" />
              </svg>
            </DropdownIcon>
          </button>
          {/* Copy invite link button */}
          <InviteUser activeDomain={activeDomain} />
        </div>

        {domainDropdownOpen && (
          <DropdownMenu>
            {domainsWithMain.map((domain, index) => (
              <DropdownLink
                key={index}
                onClick={() => handleDomainSwitch(domain)}
              >
                <div className="w-3 h-3 rounded-full bg-green-500 mr-3"></div>
                <span className="text-gray-900">{domain.name}</span>
              </DropdownLink>
            ))}
          </DropdownMenu>
        )}
      </div>
    </StartupButtonContainer>
  );
};

export default StartupButton;
